import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DataTable from "../components/DataTable";
import auditLogService from "../services/auditLogService";
import styles from "../assets/auditLog.module.css";

const AuditLog = () => {
  const [auditData, setAuditData] = useState([]);
  const [originalAuditData, setOriginalAuditData] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 30,
  });
  const [visibleColumns, setVisibleColumns] = useState({
    user: true,
    action: true,
    object: true,
    relatedTo: true,
    old: true,
    new: true,
    url: true,
    time: true,
  });
  const [showColDropdown, setShowColDropdown] = useState(false);

  useEffect(() => {
    loadFilters();
    loadAuditData({}, { currentPage: 1, recordsPerPage: 30 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFilters = async () => {
    try {
      const filterData = await auditLogService.getAuditLogFilters();
      setFilterOptions(filterData);
    } catch (error) {
      console.error("Error loading filters:", error);
    }
  };

  const loadAuditData = async (params = {}, paginationOverride = {}) => {
    setLoading(true);
    try {
      // Use override values if provided, otherwise use current state
      const currentPage = paginationOverride.currentPage ?? pagination.currentPage;
      const recordsPerPage = paginationOverride.recordsPerPage ?? pagination.recordsPerPage;

      const dataTableParams = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        draw: 1,
        ...params,
      };

      const response = await auditLogService.getAllEvents(dataTableParams);

      const data = response.data || [];
      setAuditData(data);
      setOriginalAuditData(data); // Store original data for local search
      setPagination((prev) => ({
        ...prev,
        currentPage,
        recordsPerPage,
        totalRecords: response.recordsTotal || 0,
        totalPages: Math.ceil((response.recordsTotal || 0) / recordsPerPage),
      }));
    } catch (error) {
      console.error("Error loading audit data:", error);
      setAuditData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Clear any local search term when applying server filters
    setFilters((prev) => ({ ...prev, search: { value: '' } }));
    // Reset to page 1 when filtering
    loadAuditData(filters, { currentPage: 1 });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePageChange = (page) => {
    loadAuditData(filters, { currentPage: page });
  };

  const handleEntriesChange = (event) => {
    const newEntries = parseInt(event.target.value);
    loadAuditData(filters, { currentPage: 1, recordsPerPage: newEntries });
  };

  const handleSearch = (searchTerm) => {
    setFilters((prev) => ({
      ...prev,
      search: { value: searchTerm },
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    
    // Real-time search without loading state
    if (searchTerm.trim() === '') {
      // If search is empty, restore original data
      setAuditData(originalAuditData);
    } else {
      // Filter original data in real-time
      const filteredData = originalAuditData.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        
        // Search in user name
        if (item.user && item.user.fullName && 
            item.user.fullName.toLowerCase().startsWith(searchLower)) {
          return true;
        }
        
        // Search in event/action
        if (item.event && item.event.toLowerCase().startsWith(searchLower)) {
          return true;
        }
        
        // Search in object type
        if (item.auditable_id && item.auditable_type && 
            item.auditable_type.toLowerCase().startsWith(searchLower)) {
          return true;
        }
        
        // Search in object ID
        if (item.auditable_id && item.auditable_id.toString().startsWith(searchTerm)) {
          return true;
        }
        
        // Search in URL
        if (item.url && item.url.toLowerCase().startsWith(searchLower)) {
          return true;
        }
        
        return false;
      });
      
      // Update the displayed data immediately
      setAuditData(filteredData);
    }
  };

  // Helper function to format values for export (CSV/PDF)
  const formatValuesForExport = (values) => {
    if (!values || typeof values !== 'object') return '-';

    const entries = Object.entries(values);
    if (entries.length === 0) return '-';

    // Map of known field names to human-readable labels
    const fieldLabels = {
      'role_id': 'Role', 'department_id': 'Department', 'country_id': 'Country',
      'religion_id': 'Religion', 'hear_about_id': 'Hear About', 'last_login': 'Last Login',
      'assigned_to': 'Assigned To', 'assigned_user_id': 'Assigned User', 'assigned_member_id': 'Assigned Member',
      'building_manager_id': 'Building Manager', 'notes_edited_by': 'Notes Edited By',
      'payment_notes_edited_by': 'Payment Notes Edited By',
      'status': 'Status', 'status_id': 'Status', 'status_id_guardian': 'Guardian Status',
      'urgency_id': 'Urgency', 'service_id': 'Service', 'type_id': 'Type',
      'contractor': 'Contractor', 'contractor_contact_id': 'Contractor Contact', 'client_contact_id': 'Client Contact',
      'priority': 'Priority', 'title': 'Title', 'description': 'Description',
      'first_name': 'First Name', 'last_name': 'Last Name', 'email': 'Email',
      'phone': 'Phone', 'address': 'Address', 'city': 'City', 'postcode': 'Postcode',
      'stage': 'Stage', 'notes': 'Notes', 'is_active': 'Active', 'is_verified': 'Verified',
      'guardian_id': 'Guardian', 'property_id': 'Property', 'client_id': 'Client',
      'contractor_id': 'Contractor', 'ticket_id': 'Ticket', 'amount': 'Amount',
      'due_date': 'Due Date', 'paid_date': 'Paid Date', 'payment_status': 'Payment Status',
    };

    return entries.map(([key, val]) => {
      const label = fieldLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      let displayVal = val;
      if (val === null || val === undefined) displayVal = '-';
      else if (val === '') displayVal = '(empty)';
      else if (typeof val === 'object') {
        displayVal = val.newValue !== undefined ? val.newValue : (val.oldValue !== undefined ? val.oldValue : JSON.stringify(val));
      }
      return `${label}: ${displayVal}`;
    }).join('; ');
  };

  const handleExportCSV = () => {
    const headers = [
      'User',
      'Action',
      'Object',
      'RelatedTo',
      'Old',
      'New',
      'Url',
      'Time'
    ];

    const csvData = auditData.map(item => [
      item.user ? item.user.fullName : 'System',
      item.event,
      `${item.auditable_type}: ${item.auditable_id}`,
      item.auditable_type === 'User' && item.user ? `${item.auditable_type}: ${item.user.fullName}` : '-',
      formatValuesForExport(item.old_values),
      formatValuesForExport(item.new_values),
      item.url === 'console' ? 'System' : item.url,
      new Date(item.created_at).toLocaleString('en-GB')
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_log_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Audit Log...");
      console.log("📊 [DEBUG] Audit data count:", auditData.length);
      
      // Show loading state
      const pdfButton = document.querySelector('button[onClick*="handleExportPDF"]');
      if (pdfButton) {
        pdfButton.disabled = true;
        pdfButton.innerHTML = 'Generating PDF...';
      }
      
      // Check if we have data to export
      if (!auditData || auditData.length === 0) {
        alert("No data available to export. Please check your filters or try refreshing the page.");
        return;
      }
      
      // Import jsPDF and autoTable with proper error handling
      console.log("📦 [DEBUG] Importing jsPDF...");
      const { jsPDF } = await import('jspdf');
      console.log("📦 [DEBUG] jsPDF imported successfully");
      
      console.log("📦 [DEBUG] Importing jspdf-autotable...");
      await import('jspdf-autotable');
      console.log("📦 [DEBUG] jspdf-autotable imported successfully");
      
      const doc = new jsPDF('landscape');
      console.log("📄 [DEBUG] jsPDF document created");
      
      // Add title and metadata
      doc.setFontSize(16);
      doc.text('Audit Log Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${auditData.length}`, 20, 40);
      
      // Add filters info if any
      let yPosition = 50;
      if (Object.keys(filters).length > 0) {
        doc.setFontSize(10);
        doc.text('Filters Applied:', 20, yPosition);
        yPosition += 8;
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value !== '') {
            doc.text(`${key}: ${value}`, 25, yPosition);
            yPosition += 6;
          }
        });
        yPosition += 10;
      }

      // Prepare table data
      const tableData = auditData.map(item => [
        item.user ? item.user.fullName : 'System',
        item.event,
        `${item.auditable_type}: ${item.auditable_id}`,
        item.auditable_type === 'User' && item.user ? `${item.auditable_type}: ${item.user.fullName}` : '-',
        formatValuesForExport(item.old_values),
        formatValuesForExport(item.new_values),
        item.url === 'console' ? 'System' : item.url,
        new Date(item.created_at).toLocaleString('en-GB')
      ]);

      console.log("📊 [DEBUG] Table data prepared:", tableData.length, "rows");

      // Add table with error handling
      try {
        doc.autoTable({
          head: [['User', 'Action', 'Object', 'RelatedTo', 'Old', 'New', 'Url', 'Time']],
          body: tableData,
          startY: yPosition,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            overflow: 'linebreak',
            cellWidth: 'wrap'
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          margin: { top: 10 },
          didDrawPage: (data) => {
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
            doc.setFontSize(8);
            doc.text(`Page ${currentPage} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
          }
        });
        console.log("📊 [DEBUG] Table added successfully");
      } catch (tableError) {
        console.error("❌ [ERROR] autoTable failed:", tableError);
        // Fallback: draw table manually
        doc.setFontSize(10);
        doc.text("Error generating table. Data available but table formatting failed.", 20, yPosition);
        yPosition += 20;
        
        // Add data as text
        tableData.slice(0, 20).forEach((row, index) => {
          if (yPosition > 280) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(row.join(' | '), 20, yPosition);
          yPosition += 8;
        });
      }

      // Save PDF
      const filename = `audit_log_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      console.log("✅ [SUCCESS] PDF saved:", filename);
      
      // Reset button state
      if (pdfButton) {
        pdfButton.disabled = false;
        pdfButton.innerHTML = 'Export as PDF';
      }
      
    } catch (error) {
      console.error("❌ [ERROR] PDF generation failed:", error);
      alert("Error generating PDF. Please try again.");
      
      // Reset button state
      const pdfButton = document.querySelector('button[onClick*="handleExportPDF"]');
      if (pdfButton) {
        pdfButton.disabled = false;
        pdfButton.innerHTML = 'Export as PDF';
      }
    }
  };

  const handlePrintTable = () => {
    // Implementation for printing
    window.print();
  };

  const handleColDropdownToggle = () => setShowColDropdown((v) => !v);

  const handleColDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setShowColDropdown(false);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Helper function to convert snake_case/camelCase field names to human-readable labels
  const formatFieldLabel = (fieldName) => {
    // Map of known field names to human-readable labels
    const fieldLabels = {
      'role_id': 'Role',
      'department_id': 'Department',
      'country_id': 'Country',
      'religion_id': 'Religion',
      'hear_about_id': 'Hear About',
      'last_login': 'Last Login',
      'assigned_to': 'Assigned To',
      'assigned_user_id': 'Assigned User',
      'assigned_member_id': 'Assigned Member',
      'building_manager_id': 'Building Manager',
      'notes_edited_by': 'Notes Edited By',
      'payment_notes_edited_by': 'Payment Notes Edited By',
      'status': 'Status',
      'status_id': 'Status',
      'status_id_guardian': 'Guardian Status',
      'priority': 'Priority',
      'urgency_id': 'Urgency',
      'service_id': 'Service',
      'type_id': 'Type',
      'contractor': 'Contractor',
      'contractor_contact_id': 'Contractor Contact',
      'client_contact_id': 'Client Contact',
      'title': 'Title',
      'description': 'Description',
      'first_name': 'First Name',
      'last_name': 'Last Name',
      'email': 'Email',
      'phone': 'Phone',
      'address': 'Address',
      'city': 'City',
      'postcode': 'Postcode',
      'stage': 'Stage',
      'notes': 'Notes',
      'is_active': 'Active',
      'is_verified': 'Verified',
      'guardian_id': 'Guardian',
      'property_id': 'Property',
      'client_id': 'Client',
      'contractor_id': 'Contractor',
      'ticket_id': 'Ticket',
      'amount': 'Amount',
      'due_date': 'Due Date',
      'paid_date': 'Paid Date',
      'payment_status': 'Payment Status',
      'rent_amount': 'Rent Amount',
      'deposit': 'Deposit',
      'move_in_date': 'Move In Date',
      'move_out_date': 'Move Out Date',
      'contract_start': 'Contract Start',
      'contract_end': 'Contract End',
      'created_at': 'Created At',
      'updated_at': 'Updated At',
    };

    // Return mapped label if exists, otherwise format the field name
    if (fieldLabels[fieldName]) {
      return fieldLabels[fieldName];
    }

    // Convert snake_case to Title Case
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format a value for display
  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined) return '-';
    if (value === '') return '(empty)';

    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value === 0 || value === 1) {
      // Check if this looks like a boolean field
      const booleanFields = ['is_active', 'is_verified', 'is_deleted', 'active', 'verified', 'enabled', 'disabled'];
      if (booleanFields.some(f => fieldName.toLowerCase().includes(f.replace('_', '')))) {
        return value === 1 ? 'Yes' : 'No';
      }
    }

    // Handle date fields
    const dateFields = ['date', 'login', 'created_at', 'updated_at', 'move_in', 'move_out', 'contract_start', 'contract_end', 'due_date', 'paid_date'];
    if (dateFields.some(f => fieldName.toLowerCase().includes(f.replace('_', '')))) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }

    // Handle objects/arrays
    if (typeof value === 'object') {
      // Check if it's a transformed value from Laravel presenter
      if (value.newValue !== undefined) {
        return String(value.newValue);
      }
      if (value.oldValue !== undefined) {
        return String(value.oldValue);
      }
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Helper function to render all field changes
  // valueType: 'old' for red/orange color, 'new' for green color
  const renderValueChanges = (values, styleClass, valueType = 'old') => {
    if (!values || typeof values !== 'object') return '-';

    const entries = Object.entries(values);
    if (entries.length === 0) return '-';

    // Color based on whether it's old or new value
    const valueColor = valueType === 'old' ? '#c0392b' : '#27ae60'; // Red for old, green for new

    return (
      <div className={styleClass}>
        {entries.map(([key, val], index) => {
          const label = formatFieldLabel(key);
          const displayValue = formatFieldValue(val, key);

          return (
            <div key={key} style={{ marginBottom: index < entries.length - 1 ? '4px' : 0 }}>
              <strong style={{ color: '#666' }}>{label}:</strong>{' '}
              <span style={{ color: valueColor, fontWeight: '500' }}>{displayValue}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Define table columns with visibility control
  const columns = [
    {
      Header: "User",
      accessor: "user",
      show: visibleColumns.user,
      Cell: ({ value, row }) => {
        if (value) {
          return (
            <Link to={`/users/${value.id}`} className={styles.userLink}>
              {value.fullName}
            </Link>
          );
        }
        return <span className={styles.systemLabel}>System</span>;
      },
    },
    {
      Header: "Action",
      accessor: "event",
      show: visibleColumns.action,
      Cell: ({ value }) => {
        let bgColor;
        switch (value) {
          case 'updated': bgColor = 'orange'; break;
          case 'created': bgColor = 'green'; break;
          case 'deleted': bgColor = 'darkred'; break;
          case 'restored': bgColor = 'darkgray'; break;
          default: bgColor = 'gray';
        }

        return (
          <span className={styles.actionLabel} style={{ backgroundColor: bgColor }}>
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      },
    },
    {
      Header: "Object",
      accessor: "auditable_type",
      show: visibleColumns.object,
      Cell: ({ value, row }) => (
        <div className={styles.objectCell}>
          {value}: <br />
          {row.original.auditable_id}
        </div>
      ),
    },
    {
      Header: "RelatedTo",
      accessor: "relatedTo",
      show: visibleColumns.relatedTo,
      Cell: ({ row }) => {
        const item = row.original;
        if (item.auditable_type === "User" && item.user) {
          return (
            <div className={styles.relatedToCell}>
              {item.auditable_type}: <br />
              {item.user.fullName}
            </div>
          );
        }
        if (item.monthly_inspection) {
          const context = item.monthly_inspection;
          return (
            <div className={styles.relatedToCell}>
              {context.property_name && (
                <div>Property: {context.property_name}</div>
              )}
              {context.property_address && (
                <div>{context.property_address}</div>
              )}
              {context.month && context.year && (
                <div>Report: {context.month} {context.year}</div>
              )}
            </div>
          );
        }
        return "-";
      },
    },
    {
      Header: "Old",
      accessor: "old_values",
      show: visibleColumns.old,
      Cell: ({ value }) => renderValueChanges(value, styles.oldValueCell, 'old'),
    },
    {
      Header: "New",
      accessor: "new_values",
      show: visibleColumns.new,
      Cell: ({ value }) => renderValueChanges(value, styles.newValueCell, 'new'),
    },
    {
      Header: "Url",
      accessor: "url",
      show: visibleColumns.url,
      Cell: ({ value }) => {
        if (value === "console") {
          return <span className={styles.systemLabel}>System</span>;
        }
        return <div className={styles.urlCell}>{value}</div>;
      },
    },
    {
      Header: "Time",
      accessor: "created_at",
      show: visibleColumns.time,
      Cell: ({ value }) => {
        if (!value) return "-";
        const date = new Date(value);
        // Check for Invalid Date
        if (isNaN(date.getTime())) return "-";
        return (
          <div className={styles.timeCell}>
            {date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })}
            ,{" "}
            {date.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            ({date.getFullYear()})
          </div>
        );
      },
    },
  ].filter(column => column.show);

  return (
    <div className={styles.auditLogContainer}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbContainer}>
        <nav className={styles.breadcrumb}>
          <Link to="/dashboard" className={styles.breadcrumbItem}>
            Dashboard
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbActive}>Menu</span>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbActive}>Audit log</span>
        </nav>
      </div>

      {/* Page Title */}
      <div className={styles.pageTitle}>Audit Log</div>

      <div className={styles.panelGroup}>
        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <form onSubmit={handleFilterSubmit} className={styles.filtersForm}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Featured:</label>
              <select
                className={styles.filterSelect}
                value={filters.user_id || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "user_id",
                    e.target.value === "" ? null : parseInt(e.target.value)
                  )
                }
              >
                <option value="">All</option>
                <option value="0">System</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Object</label>
              <select
                className={styles.filterSelect}
                value={filters.object_num || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "object_num",
                    e.target.value === "" ? null : parseInt(e.target.value)
                  )
                }
              >
                <option value="">All</option>
                {Object.entries(filterOptions.objects || {}).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Object ID</label>
              <input
                type="text"
                className={styles.filterInput}
                value={filters.object_id || ""}
                onChange={(e) =>
                  handleFilterChange("object_id", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Event</label>
              <select
                className={styles.filterSelect}
                value={filters.event_name || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "event_name",
                    e.target.value === "" ? null : e.target.value
                  )
                }
              >
                <option value="">All</option>
                {Object.entries(filterOptions.events || {}).map(
                  ([key, value]) => (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Date from</label>
              <div className={styles.dateInputContainer}>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={filters.date_from || ""}
                  onChange={(e) => handleFilterChange("date_from", e.target.value)}
                  placeholder="All"
                />
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Date to</label>
              <div className={styles.dateInputContainer}>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={filters.date_to || ""}
                  onChange={(e) => handleFilterChange("date_to", e.target.value)}
                  placeholder="All"
                />
              </div>
            </div>

            <button type="submit" className={styles.searchButton}>
              Search
            </button>
          </form>
        </div>

        {/* Table Controls Section */}
        <div className={styles.tableControlsSection}>
          <div className={styles.tableControlsLeft}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              Show{" "}
              <select
                className="form-control"
                value={pagination.recordsPerPage}
                onChange={handleEntriesChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
            <label>
              Search:{" "}
              <input
                type="search"
                placeholder="Search..."
                value={filters.search?.value || ""}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  marginLeft: "8px",
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  width: "400px",
                }}
              />
            </label>
          </div>

          <div className={styles.tableControlsRight}>
            <button className={styles.controlButton} onClick={handleExportCSV}>
              Export as CSV
            </button>
            <button className={styles.controlButton} onClick={handleExportPDF}>
              Export as PDF
            </button>
            <button className={styles.controlButton} onClick={handlePrintTable}>
              Print table
            </button>
            <div
              style={{
                display: "inline-block",
                position: "relative",
              }}
            >
              {showColDropdown && (
                <div
                  className={styles.colVisibilityDropdown}
                  tabIndex={-1}
                  onBlur={handleColDropdownBlur}
                >
                  <div className={styles.colVisibilityHeader}>Toggle columns:</div>
                  {Object.entries(visibleColumns).map(([key, visible]) => (
                    <div 
                      key={key} 
                      className={`${styles.colVisibilityItem} ${visible ? styles.colVisible : ''}`}
                      onClick={() => toggleColumn(key)}
                    >
                      <input 
                        type="checkbox" 
                        checked={visible} 
                        onChange={() => {}} 
                        id={`col-${key}`} 
                      />
                      <label htmlFor={`col-${key}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <button
                className={styles.controlButton}
                onClick={handleColDropdownToggle}
                tabIndex={0}
                style={{ minWidth: 120 }}
              >
                Column visibility
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className={styles.dataTableWrapper}>
          <div className={styles.dataTableLabel}>Audit Log</div>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              Loading audit data...
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={auditData}
              pagination={{
                currentPage: pagination.currentPage,
                totalPages: pagination.totalPages,
                totalRecords: pagination.totalRecords,
                recordsPerPage: pagination.recordsPerPage,
              }}
              onPageChange={handlePageChange}
              showSearch={false}
              showEntries={false}
              serverSide={true}
            />
          )}
        </div>
      </div>


    </div>
  );
};

export default AuditLog;
